import React, { useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  Form,
  Header,
  Icon,
  Label,
  Message,
  Segment,
  Table,
  TextArea,
} from 'semantic-ui-react';
import type {
  ClientCancellationLineDto,
  ClientCancellationLineReason,
} from '../../types/clientCancellationLines';
import {
  useClientCancellationLinesByContractQuery,
  useClientCancellationMutations,
} from '../../hooks/clientCancellationLines/useClientCancellationLines';
import {
  clientCancellationReasonOptions,
  labelClientCancellationReason,
  labelClientCancellationStatus,
} from '../../utils/clientCancellationLines/clientCancellationLinesLabels';
import {
  formatPlannedDateBr,
  isPlannedDateInPast,
  todayIsoDateLocal,
} from '../../utils/clientCancellationLines/clientCancellationLinesDate';
import { useToast } from '../../hooks/useToast';
import { ClientCancellationProtocolLink } from '../clientCancellationLines/ClientCancellationProtocolLink';

export interface ContractClientCancellationLineOption {
  id: string;
  ddd: string;
  numero: string;
  label?: string;
}

export interface ContractClientCancellationLinesPanelProps {
  contractId: string;
  lines: ContractClientCancellationLineOption[];
  clientId?: string | null;
  compact?: boolean;
}

function mensagemErro(error: unknown): string {
  const err = error as { response?: { data?: { message?: string } }; message?: string };
  return err?.response?.data?.message || err?.message || 'Não foi possível concluir a operação.';
}

export const ContractClientCancellationLinesPanel: React.FC<ContractClientCancellationLinesPanelProps> = ({
  contractId,
  lines,
  clientId,
  compact,
}) => {
  const { showError, showSuccess } = useToast();
  const listQuery = useClientCancellationLinesByContractQuery(contractId);
  const { createMutation, cancelMutation } = useClientCancellationMutations(contractId);

  const [contractLineId, setContractLineId] = useState('');
  const [reason, setReason] = useState<ClientCancellationLineReason>('Zerada');
  const [plannedDate, setPlannedDate] = useState(todayIsoDateLocal());
  const [notes, setNotes] = useState('');

  const lineOptions = useMemo(
    () =>
      lines
        .filter((l) => l.id)
        .map((l) => ({
          key: l.id,
          value: l.id,
          text: l.label ?? `(${l.ddd}) ${l.numero}`,
        })),
    [lines]
  );

  const entries: ClientCancellationLineDto[] = listQuery.data ?? [];

  const handleCreate = async () => {
    if (!contractLineId) {
      showError('Selecione a linha.');
      return;
    }
    if (!plannedDate) {
      showError('Informe a data planejada.');
      return;
    }
    if (isPlannedDateInPast(plannedDate)) {
      showError('A data planejada não pode ser no passado.');
      return;
    }
    try {
      await createMutation.mutateAsync({
        contractLineId,
        reason,
        plannedDate,
        notes: notes.trim() || undefined,
      });
      showSuccess('Linha registrada para cancelamento pelo cliente.');
      setNotes('');
    } catch (error) {
      showError(mensagemErro(error));
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('Cancelar este agendamento?')) return;
    try {
      await cancelMutation.mutateAsync(id);
      showSuccess('Agendamento cancelado.');
    } catch (error) {
      showError(mensagemErro(error));
    }
  };

  const segmentStyle = compact
    ? { marginTop: 16, padding: 16 }
    : {
        marginBottom: 20,
        background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)',
        border: '2px solid #FFB300',
        borderRadius: 12,
        padding: 20,
        boxShadow: '0 4px 12px rgba(255, 179, 0, 0.25)',
      };

  return (
    <Segment style={segmentStyle} data-testid="panel-linhas-cancelamento-cliente">
      <Header as="h4" style={{ color: '#5d4037', marginBottom: 12 }}>
        <Icon name="phone volume" />
        Linhas para cancelamento pelo cliente
      </Header>
      <p style={{ color: '#5d4037', marginBottom: 16, fontSize: '0.95em' }}>
        Registre linhas cujo cancelamento será feito pelo próprio cliente via TIM <strong>1056</strong>.
      </p>

      {listQuery.isError && (
        <Message negative size="small">
          Não foi possível carregar os registros. {mensagemErro(listQuery.error)}
        </Message>
      )}

      <Form loading={createMutation.isPending}>
        <Form.Group widths="equal">
          <Form.Field required>
            <label>Linha</label>
            <Dropdown
              selection
              search
              placeholder="Selecione a linha"
              options={lineOptions}
              value={contractLineId || undefined}
              onChange={(_, data) => setContractLineId(String(data.value ?? ''))}
              aria-label="Linha para cancelamento pelo cliente"
            />
          </Form.Field>
          <Form.Field required>
            <label>Motivo</label>
            <Dropdown
              selection
              options={clientCancellationReasonOptions()}
              value={reason}
              onChange={(_, data) => setReason(data.value as ClientCancellationLineReason)}
              aria-label="Motivo do cancelamento"
            />
          </Form.Field>
          <Form.Field required>
            <label>Data planejada</label>
            <input
              type="date"
              value={plannedDate}
              min={todayIsoDateLocal()}
              onChange={(e) => setPlannedDate(e.target.value)}
              aria-label="Data planejada"
            />
          </Form.Field>
        </Form.Group>
        <Form.Field>
          <label>Observações</label>
          <TextArea
            rows={2}
            value={notes}
            onChange={(_, data) => setNotes(String(data.value ?? ''))}
            placeholder="Contexto da solicitação (opcional)"
          />
        </Form.Field>
        <Button
          primary
          type="button"
          icon
          labelPosition="left"
          onClick={() => void handleCreate()}
          disabled={createMutation.isPending || lineOptions.length === 0}
        >
          <Icon name="plus" />
          Adicionar
        </Button>
      </Form>

      {listQuery.isLoading ? (
        <Message info size="small" style={{ marginTop: 16 }}>
          <Icon loading name="circle notch" /> Carregando registros…
        </Message>
      ) : entries.length === 0 ? (
        <Message info size="small" style={{ marginTop: 16 }}>
          Nenhuma linha registrada ainda.
        </Message>
      ) : (
        <Table celled compact style={{ marginTop: 16 }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Linha</Table.HeaderCell>
              <Table.HeaderCell>Motivo</Table.HeaderCell>
              <Table.HeaderCell>Data</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Protocolo</Table.HeaderCell>
              <Table.HeaderCell>Histórico</Table.HeaderCell>
              <Table.HeaderCell width={2}>Ações</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {entries.map((entry) => (
              <Table.Row key={entry.id}>
                <Table.Cell>
                  ({entry.ddd}) {entry.numeroLinha}
                </Table.Cell>
                <Table.Cell>{labelClientCancellationReason(entry.reason)}</Table.Cell>
                <Table.Cell>{formatPlannedDateBr(entry.plannedDate)}</Table.Cell>
                <Table.Cell>
                  <Label color={entry.status === 'Agendado' ? 'orange' : 'grey'}>
                    {labelClientCancellationStatus(entry.status)}
                  </Label>
                </Table.Cell>
                <Table.Cell>
                  {entry.protocolId ? (
                    <ClientCancellationProtocolLink
                      protocolId={entry.protocolId}
                      protocolNumber={entry.protocolNumber}
                      clientId={clientId}
                    />
                  ) : (
                    <span style={{ color: '#888', fontSize: '0.85em' }}>—</span>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.85em' }}>
                    {entry.statusHistory.map((h, idx) => (
                      <li key={`${entry.id}-h-${idx}`}>
                        {labelClientCancellationStatus(h.status)}
                        {h.notes ? ` — ${h.notes}` : ''}
                      </li>
                    ))}
                  </ul>
                </Table.Cell>
                <Table.Cell>
                  {entry.status === 'Agendado' && (
                    <Button
                      size="small"
                      basic
                      color="red"
                      onClick={() => void handleCancel(entry.id)}
                      loading={cancelMutation.isPending}
                    >
                      Cancelar agendamento
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Segment>
  );
};

export default ContractClientCancellationLinesPanel;
